import { Injectable, inject, signal } from '@angular/core';
import { LoggingService } from './logging.service';

export type MarketCapFilter = 'any' | 'mega' | 'large' | 'mid' | 'small' | 'micro';
export type DiscountFilter = 'any' | '20' | '50' | '80';

export interface SectorIndustryFilters {
  marketCap: MarketCapFilter;
  discount: DiscountFilter;
}

@Injectable({
  providedIn: 'root'
})
export class SectorIndustryFilterService {
  private readonly STORAGE_KEY = 'sector-industry-filters';
  private logger = inject(LoggingService);

  // Default filter values
  private readonly DEFAULT_FILTERS: SectorIndustryFilters = {
    marketCap: 'any',
    discount: 'any'
  };

  // Signals for reactive state management
  marketCapFilter = signal<MarketCapFilter>(this.DEFAULT_FILTERS.marketCap);
  discountFilter = signal<DiscountFilter>(this.DEFAULT_FILTERS.discount);

  constructor() {
    this.loadFiltersFromStorage();
  }

  /**
   * Load filter settings from localStorage
   */
  private loadFiltersFromStorage(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const filters: SectorIndustryFilters = JSON.parse(stored);

        // Update signals with stored values
        this.marketCapFilter.set(filters.marketCap ?? this.DEFAULT_FILTERS.marketCap);
        this.discountFilter.set(filters.discount ?? this.DEFAULT_FILTERS.discount);
      }
    } catch (error) {
      this.logger.warn('Failed to load sector/industry filters from localStorage:', error);
      this.resetToDefaults();
    }
  }

  /**
   * Save current filter settings to localStorage
   */
  private saveFiltersToStorage(): void {
    try {
      const filters: SectorIndustryFilters = {
        marketCap: this.marketCapFilter(),
        discount: this.discountFilter()
      };

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(filters));
    } catch (error) {
      this.logger.warn('Failed to save sector/industry filters to localStorage:', error);
    }
  }

  /**
   * Update market cap filter
   */
  updateMarketCap(marketCap: MarketCapFilter): void {
    this.marketCapFilter.set(marketCap);
    this.saveFiltersToStorage();
  }

  /**
   * Update discount filter
   */
  updateDiscount(discount: DiscountFilter): void {
    this.discountFilter.set(discount);
    this.saveFiltersToStorage();
  }

  /**
   * Reset all filters to default values
   */
  resetToDefaults(): void {
    this.marketCapFilter.set(this.DEFAULT_FILTERS.marketCap);
    this.discountFilter.set(this.DEFAULT_FILTERS.discount);

    // Clear from localStorage
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (error) {
      this.logger.warn('Failed to clear sector/industry filters from localStorage:', error);
    }
  }

  /**
   * Get current filter state as object
   */
  getCurrentFilters(): SectorIndustryFilters {
    return {
      marketCap: this.marketCapFilter(),
      discount: this.discountFilter()
    };
  }

  /**
   * Check if filters have been modified from defaults
   */
  hasCustomFilters(): boolean {
    const current = this.getCurrentFilters();
    return (
      current.marketCap !== this.DEFAULT_FILTERS.marketCap ||
      current.discount !== this.DEFAULT_FILTERS.discount
    );
  }

  /**
   * Get market cap range in dollars based on filter
   */
  getMarketCapRange(filter: MarketCapFilter): { min: number; max: number } | null {
    switch (filter) {
      case 'mega':
        return { min: 200_000_000_000, max: Number.MAX_VALUE }; // $200B and up
      case 'large':
        return { min: 10_000_000_000, max: 200_000_000_000 }; // $10B to $200B
      case 'mid':
        return { min: 2_000_000_000, max: 10_000_000_000 }; // $2B to $10B
      case 'small':
        return { min: 300_000_000, max: 2_000_000_000 }; // $300M to $2B
      case 'micro':
        return { min: 50_000_000, max: 300_000_000 }; // $50M to $300M
      case 'any':
      default:
        return null;
    }
  }

  /**
   * Get minimum discount percentage based on filter
   */
  getMinimumDiscount(filter: DiscountFilter): number | null {
    switch (filter) {
      case '20':
        return 20;
      case '50':
        return 50;
      case '80':
        return 80;
      case 'any':
      default:
        return null;
    }
  }
}
